import { API_ROOT } from './apiClient';
import axios from 'axios';
import { DEVICE_ID_HEADER, getDeviceId } from './deviceId';
import {
  AUTH_STORAGE_KEYS,
  buildTenantContext,
  clearAuthSession,
  getCrossTenantSessionDetails,
  getStoredTenantContext,
  getStoredTenantSlug,
  getStoredTenantNombre,
  isSessionCompatibleWithCurrentTenant,
  saveCrossTenantWarning,
  saveAuthSession,
} from './tenantSession';

// Cliente específico para endpoints de autenticación (prefijo /api/auth/ en lugar de /api/taller/)
const authApiClient = axios.create({
  baseURL: `${API_ROOT}/auth/`,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// Agregar token JWT en cada request de authApiClient cuando existe
authApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Identifica el dispositivo para la medición de uso de licencias. Va tanto
  // en login como en la renovación de token: sin el refresh no se puede medir
  // cuánto tiempo estuvo viva la sesión.
  config.headers[DEVICE_ID_HEADER] = getDeviceId();
  return config;
});

class AuthService {
  async login(credentials) {
    try {
      const response = await authApiClient.post('login/', credentials);
      const { access, refresh, user, tenant, tenant_nombre } = response.data;

      saveAuthSession({
        access,
        refresh,
        user,
        tenant,
        tenantNombre: tenant_nombre,
        tenantContext: buildTenantContext(process.env.REACT_APP_API_ROOT),
      });

      return { user, token: access, tenant, tenantNombre: tenant_nombre };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Canjea el código de un solo uso del login unificado (landing → subdominio)
   * por el par de tokens JWT y arranca la sesión local. El código llega en la
   * URL /acceso?code=... tras validar credenciales en el sitio comercial.
   */
  async exchangeHandoff(code) {
    const response = await axios.post(
      `${API_ROOT}/platform/login-handoff/exchange/`,
      { code },
      {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          [DEVICE_ID_HEADER]: getDeviceId(),
        },
      },
    );
    const { access, refresh, user, tenant, tenant_nombre } = response.data;

    saveAuthSession({
      access,
      refresh,
      user,
      tenant,
      tenantNombre: tenant_nombre,
      tenantContext: buildTenantContext(process.env.REACT_APP_API_ROOT),
    });

    return { user, token: access, tenant, tenantNombre: tenant_nombre };
  }

  async logout() {
    try {
      const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      if (refreshToken) {
        await authApiClient.post('logout/', { refresh: refreshToken });
      }
    } catch (error) {
      // Continuar con el logout local aunque falle el del servidor
      console.error('Error en logout del servidor:', error);
    } finally {
      clearAuthSession();
    }
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Endpoint correcto según backend/apps/users/urls.py
      const response = await authApiClient.post('token/refresh/', { refresh: refreshToken });
      const { access, refresh } = response.data;

      saveAuthSession({
        access,
        refresh,
        user: this.getCurrentUser(),
        tenant: this.getCurrentTenantSlug(),
        tenantContext: buildTenantContext(process.env.REACT_APP_API_ROOT),
      });

      return access;
    } catch (error) {
      // Sin respuesta del servidor no hubo veredicto: se cortó la red o no se
      // llega. La sesión se conserva — cerrarla dejaría a alguien que trabaja
      // sin señal fuera de la app y sin poder volver a entrar hasta recuperar
      // conexión. Sólo se cierra si el servidor rechazó el refresh de verdad.
      if (error?.response) {
        this.logout();
      }
      throw error;
    }
  }

  getCurrentUser() {
    const userData = localStorage.getItem(AUTH_STORAGE_KEYS.userData);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Relee el usuario del servidor y actualiza el guardado en el navegador.
   *
   * El usuario se persiste en el login y no se vuelve a mirar, así que un
   * cambio de rol o de permisos hecho por el administrador queda invisible
   * hasta el próximo login. Con roles de portal eso se nota: la sesión sigue
   * mostrando el menú del sistema mientras la API ya le responde 403.
   *
   * Devuelve null si falla (sin sesión, sin red): el llamador se queda con lo
   * que tenía guardado, que es lo mismo que pasaba antes.
   */
  async sincronizarUsuario() {
    try {
      const response = await authApiClient.get('me/');
      const user = response.data;
      if (user) this.updateStoredUser(user);
      return user || null;
    } catch (error) {
      return null;
    }
  }

  updateStoredUser(user) {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEYS.userData, JSON.stringify(user));
    }
  }

  getToken() {
    return localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  }

  getCurrentTenantSlug() {
    return getStoredTenantSlug();
  }

  getCurrentTenantNombre() {
    return getStoredTenantNombre();
  }

  getCurrentTenantContext() {
    return buildTenantContext(process.env.REACT_APP_API_ROOT);
  }

  getStoredTenantContext() {
    return getStoredTenantContext();
  }

  isAuthenticated() {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user && isSessionCompatibleWithCurrentTenant(process.env.REACT_APP_API_ROOT));
  }

  hasCrossTenantSession() {
    return !isSessionCompatibleWithCurrentTenant(process.env.REACT_APP_API_ROOT);
  }

  getCrossTenantSessionDetails() {
    return getCrossTenantSessionDetails(process.env.REACT_APP_API_ROOT);
  }

  registerCrossTenantWarning(source = 'auth-service') {
    const details = this.getCrossTenantSessionDetails();
    if (!details) {
      return;
    }

    saveCrossTenantWarning({
      source,
      ...details,
    });
  }

  async register(userData) {
    try {
      // Nota: el registro de usuarios solo lo hace el admin desde la página de configuración
      const response = await authApiClient.post('usuarios/', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async changePassword(passwordData) {
    try {
      // Endpoint real del backend
      const response = await authApiClient.put('cambiar-password/', passwordData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(email) {
    try {
      const response = await authApiClient.post('reset-password/', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;
